import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { API_BASE } from '@/config/api'
import { tokenRefreshed, tokensSynced, logout } from '@/store/authSlice'

// Serializa params soportando objetos anidados (e.g. searchTexts[name]=foo)
// y arrays (key=val1&key=val2). Usado por la API legacy de TKounterManager.
const paramsSerializer = (params) => {
    const search = new URLSearchParams()
    const append = (key, value) => {
        if (value === undefined || value === null || value === '') return
        if (Array.isArray(value)) {
            value.forEach((v) => append(key, v))
        } else if (typeof value === 'object') {
            Object.entries(value).forEach(([k, v]) => append(`${key}[${k}]`, v))
        } else {
            search.append(key, value)
        }
    }
    Object.entries(params || {}).forEach(([k, v]) => append(k, v))
    return search.toString()
}

const rawBaseQuery = fetchBaseQuery({
    baseUrl: API_BASE,
    timeout: 300000, // 5 min
    paramsSerializer,
    prepareHeaders: (headers, { getState }) => {
        const token = getState().auth.token
        if (token) {
            headers.set('x-access-token', token)
        }
        return headers
    }
})

// Evita que peticiones concurrentes disparen varias renovaciones a la vez:
// todas esperan a la misma promesa de refresh en curso.
let refreshPromise = null

// El refresh token rota en cada uso: si dos pestañas lo usan a la vez, la segunda
// recibe REFRESH_TOKEN_REUSED y el backend revoca TODAS las sesiones del usuario
// (incluida la de otros dispositivos/ubicaciones). Por eso, antes de golpear la red:
// 1) usamos un lock del navegador para serializar la renovación real entre pestañas
// 2) dentro del lock, releemos localStorage por si OTRA pestaña ya rotó el token
//    mientras esperábamos, y en tal caso solo sincronizamos el estado en memoria
const runRefresh = async (api) => {
    const latestRefreshToken = localStorage.getItem('refreshToken')
    if (!latestRefreshToken) return null

    const staleRefreshToken = api.getState().auth.refreshToken
    if (latestRefreshToken !== staleRefreshToken) {
        const latestToken = localStorage.getItem('token')
        if (latestToken) {
            api.dispatch(tokensSynced({ token: latestToken, refreshToken: latestRefreshToken }))
            return latestToken
        }
    }

    const refreshResult = await rawBaseQuery(
        { url: '/auth/refresh', method: 'POST', body: { refreshToken: latestRefreshToken } },
        api,
        {}
    )

    if (refreshResult.data) {
        api.dispatch(tokenRefreshed(refreshResult.data))
        return refreshResult.data.token
    }

    return null
}

const refreshAccessToken = async (api) => {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            if (typeof navigator !== 'undefined' && navigator.locks?.request) {
                return navigator.locks.request('tkounter-auth-refresh', () => runRefresh(api))
            }
            return runRefresh(api)
        })().finally(() => {
            refreshPromise = null
        })
    }

    return refreshPromise
}

// Renovación silenciosa: si el access token ha caducado (401), intenta refrescarlo
// una sola vez y reintenta la petición original. Si el refresh también falla, cierra sesión.
const baseQueryWithReauth = async (args, api, extraOptions) => {
    let result = await rawBaseQuery(args, api, extraOptions)

    // Si el 401 viene con un cuerpo no-JSON (p.ej. texto plano), fetchBaseQuery lo reporta
    // como PARSING_ERROR con el status real en originalStatus, no en status
    const is401 = result.error?.status === 401 || result.error?.originalStatus === 401

    // Un 401 en el login significa credenciales incorrectas, no token caducado:
    // se devuelve tal cual para que la pantalla muestre el error real
    const url = typeof args === 'string' ? args : args?.url
    const isSignIn = url === '/auth/signin'

    if (is401 && !isSignIn) {
        const newToken = await refreshAccessToken(api)

        if (newToken) {
            result = await rawBaseQuery(args, api, extraOptions)
        } else {
            api.dispatch(baseApi.util.resetApiState())
            api.dispatch(logout())
        }
    }

    return result
}

export const baseApi = createApi({
    reducerPath: 'api',
    baseQuery: baseQueryWithReauth,
    tagTypes: [
        'Dashboard',
        'Stores',
        'Nodes',
        'Alarms',
        'Statistics',
        'Users',
        'Catalogues',
        'CatalogueItems',
        'SearchLists',
        'SearchListItems',
        'Format',
        'InventoryGroups',
        'Inventories',
        'InventoryItems',
        'AdditionalOperations',
        'IdModification',
        'ImageSets',
        'ImageSetFiles',
        'SipOperations',
        'FloorPlans',
        'Signals',
        'Environmental',
        'Sessions',
        'Notifications'
    ],
    endpoints: () => ({})
})
